import {
  BTypeMainHead,
  BTypeCardView,
  FUNERAL_SIDO,
  DTypeBreadCrumb,
  FUNERAL_SIGUNGU,
  ATypeBeneficiaryCTA,
  colors,
  CTypeMap,
  BTypeBeneficiaryCTA,
  FullWidthButton,
} from "goi_common";
import { GetStaticPaths, GetStaticProps } from "next";

import React from "react";
import { ParlorDetailProps } from "goi_common";
import { parlorRegion } from "@/constants/parlorRegion";
import { Box, Stack } from "@mui/material";
import Head from "next/head";
import facilities from "goi_common/src/constants/facilities_detail.json";

const { regionColor, targetSidos } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = facilities
    .filter((f) => targetSidos.includes(f.sido) && f.is_public)
    .map((f) => ({
      params: {
        companyname: f.companyname + " 소개",
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const code = params?.companyname as string;
  const companyname = code.replace(" 소개", "");
  let p_facility;
  let district;
  let name = "";
  let description = "";

  if (companyname) {
    p_facility = facilities.find((f) => f.companyname === companyname);
    if (p_facility) {
      name = p_facility.companyname;

      district = p_facility.address.slice(0, 2);
      if (p_facility.address.split(" ")[0].length == 4)
        district = p_facility.address[0] + p_facility.address[2];

      description = `${p_facility.companyname} ${p_facility.address} 기초생활수급자 화장 비용 안내`;
    }
  }

  return {
    props: {
      defaultPFacility: p_facility,
      pageDescription: description,
      pageKeywords: `${p_facility?.companyname}, ${p_facility?.companyname} 기초생활수급자, ${district} 기초생활수급자 장례, 장제비, 장제급여, 기초생활수급자 장례 얼마,`,
      pageOgTitle: `${p_facility?.companyname} 기초생활수급자 혜택 공설 장지 소개 및 비용 안내 :: 부산울산경남장례정보포털`,
      pageOgDescription: description,
      pageOgImage: p_facility?.images[0].url || "",
      hasError: !p_facility,
    },
  };
};

export default function companyname({
  defaultPFacility,
  pageOgTitle,
}: {
  defaultPFacility: ParlorDetailProps;
  pageOgTitle: string;
}) {
  const regionPFacilities = facilities.filter((p) =>
    targetSidos.includes(String(p.sido)) && p.is_public
  );
  const sidoName = FUNERAL_SIDO.get(Number(defaultPFacility.sido));
  const sigunguName = FUNERAL_SIGUNGU.get(Number(defaultPFacility.sido))?.get(
    Number(defaultPFacility.sigungu)
  );
  const carouselItems = regionPFacilities.map((res, i) => {
    return {
      "@type": "ListItem",
      name: `(비용안내) ${res.companyname}`,
      image: `${res.images[0].url}`,
      url: `https://www.gyeongsang-portal.co.kr/support/free-facility/${res.companyname} 소개/`,
      position: i + 1,
    };
  });

  const carouselRichSearch =
    regionPFacilities.length > 6
      ? {
          "@context": "http://schema.org",
          "@type": "ItemList",
          itemListElement: carouselItems,
        }
      : {};
  return (
    <Stack>
      <Head>
        <title>{pageOgTitle}</title>
        {regionPFacilities.length > 5 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(carouselRichSearch),
            }}
          />
        )}
      </Head>

      <BTypeMainHead
        sidoName={sidoName}
        sigunguName={sigunguName}
        mainColor={regionColor}
        defaultParlor={defaultPFacility}
        title={`${defaultPFacility.companyname}`}
        managed_type={defaultPFacility.managed_type}
        isJangji
        event={{
          domain: "gyeongsang",
          path: "fr_facility",
          trigger_and_target: "br_crumb_facility",
        }}
      />

      <Stack
        margin="20px 12px"
        padding="24px 16px 10px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
        gap="12px"
      >
        <Stack padding="0px 20px 0px" gap="4px">
          <Box className="subtitle_18_b">필요 서류</Box>
          <Stack
            className="body_16_m"
            style={{
              background: colors.gray50,
              padding: "12px 14px",
              margin: "10px 0",
            }}
          >
            기초생활수급자는 화장 비용이 전액 감면됩니다.
          </Stack>
          <Stack gap="12px">
            <Box className="body_14_m" component="p">
              화장 비용을 전액 감면 받기 위해 기초생활수급자 자격을 증명하는
              서류가 필요합니다.
            </Box>
            <Box className="subtitle_14_b" component="p">
              필요 서류 : 국민기초생활보장수급자 증명서
            </Box>
            <Box className="body_14_m" component="p">
              수급자 증명서는 전국 시군구 또는 읍면동 주민센터에서 발급
              가능하며, 무인민원발급기(본인에 한함)와 정부24 웹사이트에서 또한
              발급 가능합니다.
            </Box>
            <Box className="body_14_m" component="p">
              기초생활수급자분들은 상기한 2가지 혜택을 받을 수 있습니다.{" "}
              <b className="subtitle_14_b">
                이해하기 어려운 부분이나 추가 정보가 필요하시다면, 언제든지
                상담을 신청해 주세요.
              </b>
            </Box>
          </Stack>
        </Stack>
        <Stack padding="20px 0">
          <FullWidthButton
            text="바로 상담하기"
            href="tel:1666-9784"
            backgroundColor={regionColor.c300}
            event={{
              domain: "gyeongsang",
              path: "fr_facility",
              trigger_and_target: "goi_call",
            }}
          />
        </Stack>
      </Stack>

      <Stack
        margin="20px 12px"
        padding="24px 16px 10px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
        gap="12px"
      >
        <BTypeBeneficiaryCTA mainColor={regionColor} />
        <Stack padding="20px 0">
          <FullWidthButton
            text="바로 상담하기"
            href="tel:1666-9784"
            backgroundColor={regionColor.c300}
            event={{
              domain: "gyeongsang",
              path: "fr_facility",
              trigger_and_target: "goi_call_btom",
            }}
          />
        </Stack>
      </Stack>

      <Stack
        margin="20px 12px"
        padding="24px 16px 20px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
        gap="12px"
      >
        <CTypeMap
          defaultParlor={defaultPFacility}
          address={defaultPFacility.address}
        />
      </Stack>

      <Stack
        margin="20px 12px"
        padding="24px 16px 10px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
        gap="12px"
      >
        <h3 className="subtitle_18_b">부산 울산 경남 기초생활수급자 장지</h3>
        {regionPFacilities.map((res) => (
          <BTypeCardView
            // descriptions={["기초생활수급자 무료"]}
            descriptions={[""]}
            mainColor={regionColor}
            key={res.companyname}
            name={res.companyname}
            href={`/support/free-facility/${res.companyname} 소개/`}
            imgSrc={res.images[0]?.url ? res.images[0].url : ""}
            event={{
              domain: "gyeongsang",
              path: "fr_facility",
              trigger_and_target: "uv_fr_facility_detail",
            }}
          />
        ))}
      </Stack>
    </Stack>
  );
}
